import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, X, Newspaper, Gift, Image } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [news, setNews] = useState([]);
  const [promos, setPromos] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  
  // Form states
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    property_name: ''
  });
  const [promoForm, setPromoForm] = useState({
    title: '',
    image_url: '',
    description: '',
    property_name: '',
    start_date: '',
    end_date: ''
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [newsRes, promosRes, propertiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/news`, config),
        axios.get(`${BACKEND_URL}/api/admin/promos`, config),
        axios.get(`${BACKEND_URL}/api/admin/properties`, config)
      ]);
      
      setNews(newsRes.data);
      setPromos(promosRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNews = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${BACKEND_URL}/api/admin/news`, {
        ...newsForm,
        property_name: newsForm.property_name === 'all' ? null : newsForm.property_name
      }, config);
      
      setSuccess('Berita berhasil dibuat!');
      setShowNewsModal(false);
      setNewsForm({ title: '', content: '', property_name: '' });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal membuat berita');
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${BACKEND_URL}/api/admin/promos`, {
        ...promoForm,
        property_name: promoForm.property_name === 'all' ? null : promoForm.property_name
      }, config);
      
      setSuccess('Promo berhasil dibuat!');
      setShowPromoModal(false);
      setPromoForm({ title: '', image_url: '', description: '', property_name: '', start_date: '', end_date: '' });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal membuat promo');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Hapus berita ini?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${BACKEND_URL}/api/admin/news/${encodeURIComponent(newsId)}`, config);
      setSuccess('Berita berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Gagal menghapus berita');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Hapus promo ini?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${BACKEND_URL}/api/admin/promos/${encodeURIComponent(promoId)}`, config);
      setSuccess('Promo berhasil dihapus!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Gagal menghapus promo');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-isk-muted">Memuat data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-isk-text">Konten Tenant</h2>
          <p className="text-sm text-isk-muted mt-1">Kelola berita dan promo untuk tenant</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {/* Sub Tabs */}
      <div className="flex space-x-2 mb-6">
        <Button
          onClick={() => setActiveTab('news')}
          variant={activeTab === 'news' ? 'default' : 'outline'}
          className={`rounded-full px-6 ${activeTab === 'news' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
        >
          <Newspaper className="w-4 h-4 mr-2" />
          Berita ({news.length})
        </Button>
        <Button
          onClick={() => setActiveTab('promos')}
          variant={activeTab === 'promos' ? 'default' : 'outline'}
          className={`rounded-full px-6 ${activeTab === 'promos' ? 'bg-isk-teal text-white' : 'border-isk-teal/30 text-isk-teal'}`}
        >
          <Gift className="w-4 h-4 mr-2" />
          Promo ({promos.length})
        </Button>
      </div>

      {/* News Tab */}
      {activeTab === 'news' && (
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-isk-text">Daftar Berita</h3>
            <Button
              onClick={() => setShowNewsModal(true)}
              className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Berita
            </Button>
          </div>

          {news.length > 0 ? (
            <div className="space-y-4">
              {news.map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white/50 border border-white/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-isk-text">{item.title}</h4>
                      <p className="text-sm text-isk-muted mt-1 whitespace-pre-wrap line-clamp-2">{item.content}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-isk-muted">
                        <span>{formatDate(item.created_at)}</span>
                        <span>{item.property_name || 'Semua Properti'}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteNews(item.created_at)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white ml-4"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-isk-muted">
              <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Belum ada berita</p>
            </div>
          )}
        </div>
      )}

      {/* Promos Tab */}
      {activeTab === 'promos' && (
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-isk-text">Daftar Promo</h3>
            <Button
              onClick={() => setShowPromoModal(true)}
              className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Promo
            </Button>
          </div>

          {promos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promos.map((promo, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden border border-white/20 bg-white/50">
                  {promo.image_url && (
                    <img 
                      src={promo.image_url} 
                      alt={promo.title}
                      className="w-full h-40 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <h4 className="font-bold text-isk-text">{promo.title}</h4>
                    {promo.description && (
                      <p className="text-sm text-isk-muted mt-1 line-clamp-2">{promo.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-isk-muted">
                        {promo.property_name || 'Semua Properti'}
                      </span>
                      <Button
                        onClick={() => handleDeletePromo(promo.created_at)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {(promo.start_date || promo.end_date) && (
                      <p className="text-xs text-isk-gold mt-2">
                        {promo.start_date && formatDate(promo.start_date)} - {promo.end_date && formatDate(promo.end_date)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-isk-muted">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Belum ada promo</p>
            </div>
          )}
        </div>
      )}

      {/* News Modal */}
      {showNewsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Tambah Berita</h3>
              <button onClick={() => setShowNewsModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateNews} className="space-y-4">
              <div className="space-y-2">
                <Label>Judul *</Label>
                <Input
                  type="text"
                  required
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                  placeholder="Judul berita"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Konten *</Label>
                <Textarea
                  required
                  value={newsForm.content}
                  onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                  placeholder="Isi berita..."
                  className="glass-input border-2 focus:border-isk-teal rounded-xl min-h-[150px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Untuk Properti</Label>
                <Select
                  value={newsForm.property_name}
                  onValueChange={(value) => setNewsForm({ ...newsForm, property_name: value })}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                    <SelectValue placeholder="Pilih properti (atau semua)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Properti</SelectItem>
                    {properties.map((prop, idx) => (
                      <SelectItem key={idx} value={prop.name}>{prop.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowNewsModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                >
                  Simpan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promo Modal */}
      {showPromoModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Tambah Promo</h3>
              <button onClick={() => setShowPromoModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreatePromo} className="space-y-4">
              <div className="space-y-2">
                <Label>Judul Promo *</Label>
                <Input
                  type="text"
                  required
                  value={promoForm.title}
                  onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                  placeholder="Judul promo"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>URL Gambar *</Label>
                <Input
                  type="url"
                  required
                  value={promoForm.image_url}
                  onChange={(e) => setPromoForm({ ...promoForm, image_url: e.target.value })}
                  placeholder="https://example.com/gambar.jpg"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
                {promoForm.image_url && (
                  <div className="mt-2">
                    <img 
                      src={promoForm.image_url} 
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-xl"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Deskripsi</Label>
                <Textarea
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                  placeholder="Deskripsi promo (opsional)"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Untuk Properti</Label>
                <Select
                  value={promoForm.property_name}
                  onValueChange={(value) => setPromoForm({ ...promoForm, property_name: value })}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                    <SelectValue placeholder="Pilih properti (atau semua)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Properti</SelectItem>
                    {properties.map((prop, idx) => (
                      <SelectItem key={idx} value={prop.name}>{prop.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mulai Tanggal</Label>
                  <Input
                    type="date"
                    value={promoForm.start_date}
                    onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value })}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sampai Tanggal</Label>
                  <Input
                    type="date"
                    value={promoForm.end_date}
                    onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })}
                    className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowPromoModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                >
                  Simpan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
